import { SILHOUETTE_PIPELINE } from './silhouette.pipeline';
import pointInPolygon from 'point-in-polygon';
import { TSilhouetteModel } from './silhouette.types';
import { TRESPONSE_GetSilhouetteOperations } from '@monorepo/types';

export const SILHOUETTE_MODEL = {
  createModel: (
    mode: TSilhouetteModel['mode'],
    image: string,
    apiResponse:
      | TRESPONSE_GetSilhouetteOperations['backImageOperation']
      | TRESPONSE_GetSilhouetteOperations['frontImageOperation'],
  ): TSilhouetteModel => {
    return {
      mode,
      areas: apiResponse.silhouetteOperations.map((a) => ({
        id: a.silhouetteOperationId,
        coordinateByPercent: a.polygon.coordinates.map((c) => [c.x, c.y]),
        items: a.operations?.length ? a.operations : a.operationIds.map((id) => ({ id, name: '' })),
        centroid: [0, 0],
      })),
      editingArea: null,
      hasChanged: false,
      image: image,
    };
  },
  addAreaByCoordinate: (
    coordinateByPercent: TSilhouetteModel['areas'][number]['coordinateByPercent'],
    silhouetteModel: TSilhouetteModel,
  ): TSilhouetteModel => {
    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      mode: 'DRAWING',
      editingArea: {
        id: Math.random().toString(),
        coordinateByPercent: [...coordinateByPercent],
        items: [],
      },
    });
  },
  pasteArea: (copiedArea: TSilhouetteModel['areas'][number], silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      editingArea: {
        ...copiedArea,
        id: Math.random().toString(),
      },
    });
  },
  updateArea: (areaInfo: TSilhouetteModel['areas'][number], silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      editingArea: areaInfo,
    });
  },
  setActiveArea: (coordinate: [number, number], silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    const editingArea = silhouetteModel.areas.find((v) => pointInPolygon(coordinate, v.coordinateByPercent));

    // If no edit area is found
    if (!editingArea) return silhouetteModel;

    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      areas: silhouetteModel.areas.map((v) => {
        return {
          ...v,
          isEditing: pointInPolygon(coordinate, v.coordinateByPercent),
        };
      }),
      editingArea,
    });
  },
  setEditingArea: (areaId: string, silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    const editingArea = silhouetteModel.areas.find((v) => v.id === areaId);
    // If no edit area is found
    if (!editingArea) {
      console.log('[ERROR] No Edit area found');
      return silhouetteModel;
    }

    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      editingArea,
    });
  },
  disableAddArea: (silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      disabled: true,
      editingArea: null,
    });
  },
  enableAddArea: (silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      disabled: false,
      editingArea: null,
    });
  },
  removeEditArea: (silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    const hasEditArea = silhouetteModel.areas.find((v) => v.id === silhouetteModel.editingArea?.id);

    if (hasEditArea) {
      return silhouetteModel;
    } else {
      return SILHOUETTE_PIPELINE.computePoints({
        ...silhouetteModel,
        editingArea: null,
      });
    }
  },
  clearEditArea: (silhouetteModel: TSilhouetteModel): TSilhouetteModel =>
    SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      editingArea: null,
    }),

  commitArea: (silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    if (!silhouetteModel.editingArea) return SILHOUETTE_PIPELINE.computePoints({ ...silhouetteModel, disabled: false });

    const hasEditArea = silhouetteModel.areas.find((v) => v.id === silhouetteModel.editingArea?.id);

    if (!hasEditArea) {
      return SILHOUETTE_PIPELINE.computePoints({
        ...silhouetteModel,
        areas: [...silhouetteModel.areas, silhouetteModel.editingArea],
        editingArea: null,
        disabled: false,
      });
    }
    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      areas: silhouetteModel.areas.map((v) => {
        if (!silhouetteModel.editingArea) return v;
        return v.id === silhouetteModel.editingArea.id
          ? {
              ...silhouetteModel.editingArea,
            }
          : v;
      }),
      editingArea: null,
      disabled: false,
    });
  },
  removeEditingArea: (silhouetteModel: TSilhouetteModel): TSilhouetteModel => {
    return SILHOUETTE_PIPELINE.computePoints({
      ...silhouetteModel,
      editingArea: null,
    });
  },

  addItems: <T>(areaItems: T[], silhouetteModel: TSilhouetteModel<T>): TSilhouetteModel<T> => {
    return {
      ...silhouetteModel,
      editingArea: silhouetteModel.editingArea
        ? {
            ...silhouetteModel.editingArea,
            items: [...areaItems],
          }
        : null,
    };
  },
  keyBoardEventActiveArea: (
    keyboardEvents: 'ArrowUp' | 'ArrowLeft' | 'ArrowDown' | 'ArrowRight',
    silhouetteModel: TSilhouetteModel,
  ): TSilhouetteModel => {
    if (!silhouetteModel.editingArea) return silhouetteModel;

    // Constraints can be moved to a function
    let move = [0, 0];
    switch (keyboardEvents) {
      case 'ArrowUp':
        move = [0, -0.5];
        break;
      case 'ArrowDown':
        move = [0, 0.5];
        break;
      case 'ArrowLeft':
        move = [-0.5, 0];
        break;
      case 'ArrowRight':
        move = [0.5, 0];
        break;
      default:
        break;
    }

    const updatedCoordinates: [number, number][] = silhouetteModel.editingArea.coordinateByPercent.map((c) => [
      c[0] + move[0],
      c[1] + move[1],
    ]);

    const updatedEditArea: NonNullable<TSilhouetteModel['editingArea']> = {
      ...silhouetteModel.editingArea,
      coordinateByPercent: updatedCoordinates,
    };

    const hasAtleastOnCorrdinateInside =
      updatedEditArea.coordinateByPercent.filter(([x, y]) => x > 5 && x < 100 && y > 5 && y < 100).length >= 1;

    if (!hasAtleastOnCorrdinateInside) {
      return silhouetteModel;
    }

    return {
      ...silhouetteModel,
      editingArea: updatedEditArea,
    };
  },
};
